// Enrolment: the device makes its own key, registers it with a one-time code, and shows a
// fingerprint that the approving administrator compares.
import { exportPublicKey, generateKeyPair } from "@vavr/core";
import type { VavrDb, DeviceRecord } from "./db.ts";
import { ApiError, DeviceApi } from "./api.ts";

export async function enrol(db: VavrDb, apiBase: string, code: string, appVersion: string, fetchImpl: typeof fetch = (...a) => fetch(...a)): Promise<DeviceRecord> {
  const keys = await generateKeyPair(false);
  const mirrorKey = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
  const res = await fetchImpl(`${apiBase}/api/v1/devices/enrol`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, pubkey: await exportPublicKey(keys.publicKey), app_version: appVersion }),
  });
  const data = await res.json() as any;
  if (!res.ok) throw new ApiError(res.status, data.error, data.message);
  const rec: DeviceRecord = {
    key: "device", device_id: data.device_id, site_id: data.site_id, kind: data.kind, status: data.status === "active" ? "active" : "pending", fingerprint: data.fingerprint,
    keys, mirrorKey, api_base: apiBase, enrolled_at: Date.now(),
  };
  await db.device.put(rec);
  return rec;
}

/**
 * The enrolled device, if any. An app built for a server (`apiBase` set) follows that build: a
 * record enrolled under an older address of the same server (staging moved to stage.vavr.us) is
 * rewritten to it, keeping its key and enrolment. The website passes "" (same origin) and keeps
 * whatever it enrolled with.
 */
export async function loadDevice(db: VavrDb, apiBase = ""): Promise<DeviceRecord | undefined> {
  const rec = await db.device.get("device");
  if (rec && apiBase && rec.api_base !== apiBase) {
    rec.api_base = apiBase;
    await db.device.put(rec);
  }
  return rec;
}

/** Asks the server for this device's status; a revoked device wipes its mirror and key (plan §7). */
export async function refreshStatus(db: VavrDb, api: DeviceApi) {
  try {
    const me = await api.json("GET", "/api/v1/device/me");
    await db.device.update("device", { status: me.device.status });
    await db.meta.put({ key: "site", value: me.site });
    return me as { device: { status: string }; site: SiteInfo };
  } catch (e) {
    if (e instanceof ApiError && (e.code === "device_revoked" || e.code === "unknown_device")) {
      await wipe(db);
      return null;
    }
    throw e;
  }
}

export interface SiteInfo {
  id: string; name: string; tz: string; state: string; sign_pubkey: string;
  settings: { dropoff_signature: "required" | "optional" | "off"; adult_pins: boolean; attestation_pickup: string; attestation_dropoff: string; closing_time: string | null; pickup_signature?: "required" | "optional"; pickup_photo?: "required" | "optional"; reunification_at?: number | null; reunification_place?: string | null };
}

/** Removes the roster mirror and the device key. Unsent events are kept: evidence is never dropped. */
export async function wipe(db: VavrDb) {
  await db.roster.clear();
  await db.device.clear();
  await db.meta.delete("site");
}
